/**
 * The desktop's menu bar: every top-level folder is a menu (the shared
 * `NavMenuBar`, cascading to any depth), followed by a "Window" menu that
 * lists the open screens and the Open editors.
 *
 * Top-level folders that do not fit fold into a trailing "More" menu instead
 * of being clipped — the bar sits in the host's top bar next to the app title,
 * Search and the user menu, and a large menu must not push those off screen.
 * "More" is a synthetic folder node, so the shared menu draws it with the same
 * keyboard model as the real ones.
 */
import { Check, ChevronDown, MoreHorizontal, X } from 'lucide-react'
import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type KeyboardEvent, type RefObject,
} from 'react'
import { tw } from '../../../testHooks'
import { EditorMark, UnsavedDot, editorTitle } from '../../nav/parts/EditorsList'
import { NavMenuBar } from '../../nav/parts/Menus'
import { NavIcon } from '../../nav/navIcons'
import { S, iconButton } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'
import { menuPanel } from './styles'

const MORE_ID = '__shell_mac_more'
/** Gap between menubar items — NavMenuBar's own. */
const ITEM_GAP = 2
/** Padding around the (clipping) menus box so a focus ring is not cut off. */
const FOCUS_ROOM = 3

function moreNode(children: NavTreeNode[]): NavTreeNode {
  return {
    item: { id: MORE_ID, parent_id: '', obj_type: '-', obj_name: '', title: 'More' },
    children, depth: 0, path: [], isContainer: true, isFolder: true,
  }
}

/** The shown editor: the active session, when it is on the current screen. */
export function shownEditorOf(model: ShellNavModel) {
  const a = model.activeEditor
  return a && model.current && a.objName === model.current.obj_name ? a : null
}

export interface WindowAction {
  key: string
  label: string
  disabled?: boolean
  run: () => void
}

export interface MacMenuBarProps {
  model: ShellNavModel
  windowOpen: boolean
  setWindowOpen: (open: boolean) => void
  /** The layout's own Window entries (tiling, minimise, Desktop & Dock). */
  windowActions?: WindowAction[]
}

export function MacMenuBar({ model, windowOpen, setWindowOpen, windowActions = [] }: MacMenuBarProps) {
  const outerRef = useRef<HTMLDivElement>(null)
  const areaRef = useRef<HTMLDivElement>(null)
  const windowRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const roots = model.roots
  const [fit, setFit] = useState(roots.length)
  // Re-measure when the labels change, not on every model render.
  const sig = roots.map((r) => `${r.item.id}\u0001${r.item.title}\u0001${r.item.icon || ''}`).join('\u0002')

  useLayoutEffect(() => {
    const outer = outerRef.current
    const meas = measureRef.current
    if (!outer || !meas) return undefined
    // The room is the whole slot less the Window menu — measured on the
    // OUTER box, because the menus' own box shrinks to what it shows and
    // would never report that space has come back.
    const compute = () => {
      const avail = outer.clientWidth - (windowRef.current?.offsetWidth || 0) - ITEM_GAP - 2 * FOCUS_ROOM
      const widths = [...meas.children].map((c) => (c as HTMLElement).offsetWidth)
      const moreW = widths.pop() || 0
      const n = widths.length
      const total = widths.reduce((a, w) => a + w, 0) + ITEM_GAP * Math.max(0, n - 1)
      let k = n
      if (total > avail) {
        let acc = moreW
        k = 0
        while (k < n && acc + ITEM_GAP + widths[k] <= avail) { acc += ITEM_GAP + widths[k]; k += 1 }
      }
      setFit((prev) => (prev === k ? prev : k))
    }
    compute()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(compute)
    ro.observe(outer)
    return () => ro.disconnect()
  }, [sig])

  const shown = useMemo(() => {
    if (fit >= roots.length) return roots
    return [...roots.slice(0, fit), moreNode(roots.slice(fit))]
  }, [roots, fit])

  const focusLastMenu = useCallback(() => {
    const items = areaRef.current?.querySelectorAll<HTMLElement>('[role="menubar"] > [role="menuitem"]')
    items?.[items.length - 1]?.focus()
  }, [])

  const labelStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 10px', fontSize: 14, whiteSpace: 'nowrap' } as const

  return (
    <div ref={outerRef} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: ITEM_GAP }}>
      <div ref={areaRef} style={{ position: 'relative', flex: '0 1 auto', minWidth: 0, overflow: 'hidden', display: 'flex', padding: FOCUS_ROOM }}>
        {roots.length > 0 && (
          <NavMenuBar
            model={model}
            nodes={shown}
            label="Menu bar"
            renderItemLabel={(n) => (n.item.id === MORE_ID
              ? <><MoreHorizontal size={15} aria-hidden />More</>
              : <><NavIcon node={n.item} size={15} />{n.item.title}</>)}
          />
        )}
        {/* Off-screen replica of the items, measured to decide what fits. */}
        <div ref={measureRef} aria-hidden style={{
          position: 'absolute', left: 0, top: 0, visibility: 'hidden', pointerEvents: 'none',
          display: 'flex', whiteSpace: 'nowrap', fontFamily: 'inherit',
        }}>
          {roots.map((r) => (
            <span key={r.item.id} style={labelStyle}><NavIcon node={r.item} size={15} />{r.item.title}</span>
          ))}
          <span style={labelStyle}><MoreHorizontal size={15} />More</span>
        </div>
      </div>
      <div ref={windowRef} style={{ display: 'flex', flexShrink: 0 }}>
        <WindowMenu model={model} open={windowOpen} setOpen={setWindowOpen} onLeft={focusLastMenu} extra={windowActions} />
      </div>
    </div>
  )
}

interface Row {
  key: string
  label: string
  disabled?: boolean
  run: () => void
}

/**
 * Window: Minimise editor / Enter full screen / Close window, the open
 * screens (the current one checked — only one window is live, picking another
 * brings it forward), and the Open editors with their unsaved dot and ×.
 */
function WindowMenu({ model, open, setOpen, onLeft, extra }: {
  model: ShellNavModel
  open: boolean
  setOpen: (open: boolean) => void
  onLeft: () => void
  extra: WindowAction[]
}) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const count = model.editorsEnabled ? model.editors.length : 0
  const current = model.current
  const shownEditor = shownEditorOf(model)

  useLayoutEffect(() => {
    if (!open || !btnRef.current) { setPos(null); return }
    const r = btnRef.current.getBoundingClientRect()
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - 392)), top: r.bottom + 4 })
  }, [open])

  // Focus the first entry once drawn (opened by click, key or "Open editors").
  useEffect(() => {
    if (!open || !pos) return
    items(menuRef)[0]?.focus()
  }, [open, pos])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open, setOpen])

  const close = (refocus: boolean) => { setOpen(false); if (refocus) btnRef.current?.focus() }
  const pick = (fn: () => void) => { fn(); setOpen(false) }

  const actions: Row[] = [
    {
      key: 'minimise-editor', label: 'Minimise editor', disabled: !shownEditor,
      run: () => { if (shownEditor) model.minimiseEditor(shownEditor.id) },
    },
    { key: 'fullscreen', label: 'Enter full screen', disabled: !current, run: () => model.setFullScreen(true) },
    { key: 'close-window', label: 'Close window', disabled: !current, run: () => { if (current) model.closeScreen(current) } },
    ...extra,
  ]

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = items(menuRef)
    const i = els.indexOf(document.activeElement as HTMLElement)
    const go = (j: number) => { e.preventDefault(); els[(j + els.length) % els.length]?.focus() }
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break
      case 'ArrowUp': go(i - 1); break
      case 'Home': go(0); break
      case 'End': go(els.length - 1); break
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(true); break
      case 'ArrowLeft': e.preventDefault(); close(false); onLeft(); break
      case 'Tab': close(false); break
      default:
    }
  }

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '0 10px',
    border: 'none', borderRadius: 7, background: 'transparent', color: S.text, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 14.5, textAlign: 'left',
  } as const
  const heading = (text: string) => (
    <div role="presentation" style={{ padding: '8px 10px 4px', fontSize: 13, fontWeight: 600, color: S.text2 }}>{text}</div>
  )
  const sep = <div role="separator" style={{ height: 1, margin: '4px 6px', background: S.border }} />

  return (
    <div role="menubar" aria-label="Window" style={{ display: 'flex', flexShrink: 0 }}>
      <button
        ref={btnRef}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={count ? `Window, ${count} open editor${count === 1 ? '' : 's'}` : 'Window'}
        className="tw-sh-btn"
        {...tw('action', 'shell-mac-window-menu', { count: count || undefined })}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true) }
          if (e.key === 'ArrowLeft') { e.preventDefault(); onLeft() }
          if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false) }
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 10px',
          border: 'none', borderRadius: 6, background: open ? S.surface2 : 'transparent',
          color: S.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, whiteSpace: 'nowrap',
        }}
      >
        Window
        {count > 0 && (
          <span aria-hidden style={{
            minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: S.accent, color: '#fff',
            fontSize: 13, fontWeight: 600, lineHeight: '20px', textAlign: 'center', boxSizing: 'border-box',
          }}>{count}</span>
        )}
        <ChevronDown size={14} aria-hidden color={S.faint} />
      </button>
      {open && pos && (
        <div ref={menuRef} role="menu" aria-label="Window" data-tw-shell-menu="" onKeyDown={onMenuKey}
          style={{ ...menuPanel, position: 'fixed', left: pos.left, top: pos.top, zIndex: 9441 }}>
          {actions.map((a) => (
            <div key={a.key} role="none">
              <button type="button" role="menuitem" disabled={a.disabled} aria-disabled={a.disabled || undefined}
                className="tw-sh-item tw-mac-row" {...tw('action', `shell-mac-${a.key}`)}
                onClick={() => pick(a.run)} style={{ ...rowStyle, paddingLeft: 34 }}>
                {a.label}
              </button>
            </div>
          ))}
          {sep}
          {heading(`Open screens · ${model.openScreens.length}`)}
          {model.openScreens.length === 0 && (
            <div role="presentation" style={{ padding: '4px 10px 8px 34px', fontSize: 14, color: S.text2 }}>No open screens</div>
          )}
          {model.openScreens.map((item) => {
            const on = current?.id === item.id
            return (
              <div key={item.id} role="none">
                <button type="button" role="menuitemradio" aria-checked={on} className="tw-sh-item tw-mac-row"
                  {...tw('nav', item.obj_name, { type: item.obj_type, selected: on })}
                  onClick={() => pick(() => model.open(item))} style={{ ...rowStyle, fontWeight: on ? 600 : 400 }}>
                  <span aria-hidden style={{ width: 14, display: 'inline-flex', flexShrink: 0 }}>
                    {on && <Check size={14} strokeWidth={2.6} />}
                  </span>
                  <NavIcon node={item} size={16} color={on ? S.accent : S.text2} />
                  <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
                </button>
              </div>
            )
          })}

          {model.editorsEnabled && (
            <>
              {sep}
              {heading(`Open editors · ${model.editors.length}`)}
              {model.editors.length === 0 && (
                <div role="presentation" style={{ padding: '4px 10px 8px 34px', fontSize: 14, color: S.text2 }}>
                  No open editors
                </div>
              )}
              {model.editors.map((s) => {
                const active = s.id === model.activeEditorId
                const screen = model.itemFor(s.objName)?.title || s.objName
                const title = editorTitle(s)
                return (
                  <div key={s.id} role="none" style={{
                    display: 'flex', alignItems: 'center', gap: 2, borderRadius: 7,
                    background: active ? S.surface2 : 'transparent',
                  }}>
                    <button
                      type="button"
                      role="menuitem"
                      className="tw-sh-item tw-mac-row"
                      aria-current={active ? 'true' : undefined}
                      aria-keyshortcuts="Delete"
                      aria-label={`${s.mode === 'add' ? 'New' : 'Editing'} ${title}, ${screen}${s.dirty ? ', unsaved changes' : ''}`}
                      {...tw('action', 'shell-editor', { obj: s.objName, mode: s.mode, txn: s.txnId, dirty: s.dirty })}
                      onClick={() => pick(() => model.restoreEditor(s.id))}
                      // Delete closes the entry from the keyboard (× is mouse-sized).
                      onKeyDown={(e) => { if (e.key === 'Delete') { e.preventDefault(); model.closeEditor(s.id) } }}
                      style={{ ...rowStyle, flex: 1, minWidth: 0, minHeight: 50 }}
                    >
                      <EditorMark session={s} model={model} size={28} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="tw-sh-clip" style={{ display: 'block', fontSize: 14.5, fontWeight: 500 }}>{title}</span>
                        <span className="tw-sh-clip" style={{ display: 'block', fontSize: 13, color: S.text2 }}>{screen}</span>
                      </span>
                      {s.dirty && <UnsavedDot />}
                    </button>
                    <button type="button" tabIndex={-1} className="tw-sh-btn"
                      onClick={() => model.closeEditor(s.id)} aria-label={`Close ${title}`} title="Close"
                      {...tw('action', 'shell-editor-close', { obj: s.objName, txn: s.txnId })}
                      style={{ ...iconButton(30), marginRight: 4 }}>
                      <X size={14} aria-hidden />
                    </button>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function items(ref: RefObject<HTMLDivElement | null>): HTMLElement[] {
  return [...(ref.current?.querySelectorAll<HTMLElement>(
    ':scope > [role="none"] > [role="menuitem"]:not(:disabled), :scope > [role="none"] > [role="menuitemradio"]',
  ) || [])]
}
